"use client";

import React from "react";
import { ArrowRight, BookOpen, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TrainingAcademyTeaser() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="training-academy-teaser" className="dark bg-background text-foreground relative overflow-hidden py-24 sm:py-32">
      {}
      <style>{`
        @keyframes float-smooth {
          0% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-15px) rotate(2deg); }
          100% { transform: translateY(0px) rotate(0deg); }
        }
        .animate-float-smooth {
          animation: float-smooth 6s ease-in-out infinite;
        }
        @keyframes mesh-move-1 {
          0% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(5%, 5%) scale(1.05); }
          66% { transform: translate(-5%, 10%) scale(0.95); }
          100% { transform: translate(0, 0) scale(1); }
        }
        @keyframes mesh-move-2 {
          0% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(-5%, -10%) scale(0.95); }
          66% { transform: translate(10%, -5%) scale(1.05); }
          100% { transform: translate(0, 0) scale(1); }
        }
        .animate-mesh-1 {
          animation: mesh-move-1 20s ease-in-out infinite;
        }
        .animate-mesh-2 {
          animation: mesh-move-2 25s ease-in-out infinite reverse;
        }
      `}</style>

      {}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -left-[10%] w-[70%] h-[70%] rounded-full bg-primary/20 blur-[120px] animate-mesh-1 opacity-60" />
        <div className="absolute top-[40%] -right-[20%] w-[80%] h-[80%] rounded-full bg-blue-900/30 blur-[150px] animate-mesh-2 opacity-50" />
        <div className="absolute -bottom-[20%] left-[20%] w-[60%] h-[60%] rounded-full bg-indigo-900/20 blur-[100px] animate-mesh-1 opacity-40" style={{
        animationDelay: '-5s'
      }} />
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
        <div ref={ref} className={`transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {}
          <div className="relative rounded-3xl bg-card/10 backdrop-blur-xl border border-white/10 shadow-2xl overflow-hidden">
            {}
            <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent pointer-events-none" />
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center p-8 sm:p-12 lg:p-16">
              
              {}
              <div className="relative flex justify-center items-center order-2 lg:order-1">
                {}
                <div className="absolute inset-0 bg-gradient-to-tr from-primary/20 to-transparent rounded-full blur-3xl transform scale-75" />
                
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1588353/img/training_manual_3d_mockup_dark.png" alt="GigFleet Training Manual 3D Mockup" className="relative z-10 w-full max-w-md object-contain drop-shadow-2xl animate-float-smooth" style={{
                filter: 'drop-shadow(0 25px 25px rgba(0,0,0,0.5))'
              }} />
                
                {}
                <div className="absolute top-10 -left-4 sm:left-0 bg-background/80 backdrop-blur-md border border-white/10 rounded-2xl p-4 shadow-xl animate-float-smooth" style={{
                animationDelay: '-2s'
              }}>
                  <div className="flex items-center gap-3">
                    <div className="bg-primary/20 p-2 rounded-lg text-primary">
                      <BookOpen className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground font-medium">Instant Access</p>
                      <p className="text-sm font-bold text-foreground">Digital Download</p>
                    </div>
                  </div>
                </div>

                <div className="absolute bottom-10 -right-4 sm:right-0 bg-background/80 backdrop-blur-md border border-white/10 rounded-2xl p-4 shadow-xl animate-float-smooth" style={{
                animationDelay: '-4s'
              }}>
                  <div className="flex items-center gap-3">
                    <div className="bg-emerald-500/20 p-2 rounded-lg text-emerald-400">
                      <TrendingUp className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground font-medium">Proven Strategies</p>
                      <p className="text-sm font-bold text-foreground">Scale Your Fleet</p>
                    </div>
                  </div>
                </div>
              </div>

              {}
              <div className="flex flex-col items-start order-1 lg:order-2">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-semibold tracking-wide mb-6">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
                  </span>
                  GigFleet Training Academy
                </div>
                
                <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-foreground mb-6 leading-[1.1]">
                  From Driver to <br className="hidden sm:block" />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-blue-400">
                    Fleet Owner
                  </span>
                </h2>
                
                <p className="text-lg sm:text-xl text-muted-foreground mb-8 max-w-xl leading-relaxed">
                  Stop trading time for money. Learn the exact blueprint to build, manage, and scale a profitable car rental business for the gig economy.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
                  <Button size="lg" className="h-14 px-8 text-base font-semibold rounded-xl shadow-[0_0_20px_rgba(var(--color-primary),0.3)] hover:shadow-[0_0_30px_rgba(var(--color-primary),0.5)] transition-all" asChild>
                    <Link to="/TrainingAcademyPage">
                      Explore the Academy
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Link>
                  </Button>
                  <Button size="lg" variant="outline" className="h-14 px-8 text-base font-semibold rounded-xl border-white/20 hover:bg-white/5 transition-all" asChild>
                    <Link to="/DriverFAQPage">
                      Read the FAQ
                    </Link>
                  </Button>
                </div>

                <div className="mt-10 flex items-center gap-4 text-sm text-muted-foreground">
                  <div className="flex -space-x-2">
                    {[1, 2, 3, 4].map(i => <img key={i} data-index={i} className="inline-block h-8 w-8 rounded-full ring-2 ring-background object-cover" src={`/img/avatar_student_${i}.png`} alt={`Student ${i}`} />)}
                  </div>
                  <p>Join <strong className="text-foreground">500+</strong> entrepreneurs</p>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </section>;
}